import { Coffee, Building2, Calculator, ArrowRight, Sparkles } from 'lucide-react';

const products = [
  {
    id: 'communityos',
    name: 'FreeCoffee CommunityOS',
    tagline: 'Your Free CommunityOS',
    description: 'Complete community management solution for apartments, societies, and gated communities. Manage maintenance, residents, payments, and more.',
    icon: Building2,
    color: 'emerald',
    link: 'https://freecoffee.in/login',
    features: ['Maintenance Management', 'Resident Portal', 'Online Payments', 'Announcements']
  },
  {
    id: 'accountant',
    name: 'FreeCoffee Accountant',
    tagline: 'Smart Financial Management',
    description: 'Professional accounting and bookkeeping solution for businesses. Track expenses, manage invoices, and generate financial reports.',
    icon: Calculator,
    color: 'amber',
    link: 'https://acc.freecoffee.in/login',
    features: ['Expense Tracking', 'Invoice Management', 'Financial Reports', 'Multi-Business']
  }
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="landing-page">
      {/* Animated background pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-amber-600/10 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="pt-8 pb-4 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-center">
            <div className="flex items-center gap-3">
              {/* Coffee Cup Logo */}
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Coffee size={28} className="text-white" />
                </div>
                {/* Steam animation */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex gap-1">
                  <div className="w-1 h-3 bg-amber-400/40 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                  <div className="w-1 h-4 bg-amber-400/30 rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
                  <div className="w-1 h-3 bg-amber-400/40 rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight">
                  Free<span className="text-amber-400">Coffee</span>
                </h1>
                <p className="text-xs text-slate-400 -mt-0.5">Brewing Better Solutions</p>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-6">
              <Sparkles size={14} className="text-amber-400" />
              <span className="text-amber-300 text-xs font-medium">Simple • Powerful • Free</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight mb-4 max-w-3xl mx-auto leading-tight">
              Choose Your{' '}
              <span className="bg-gradient-to-r from-amber-400 to-amber-500 bg-clip-text text-transparent">
                Perfect Brew
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-xl mx-auto">
              Two powerful solutions crafted for your success. Pick the one that fits your needs.
            </p>
          </div>

          {/* Product Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl w-full px-4">
            {products.map((product) => (
              <a
                key={product.id}
                href={product.link}
                className="group relative bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-slate-600 transition-all duration-300 hover:shadow-xl hover:shadow-slate-900/50 hover:-translate-y-1"
                data-testid={`product-card-${product.id}`}
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${
                  product.color === 'emerald' 
                    ? 'from-emerald-500/5 to-emerald-500/10' 
                    : 'from-amber-500/5 to-amber-500/10'
                } opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl ${
                    product.color === 'emerald' 
                      ? 'bg-emerald-500/10 border border-emerald-500/20' 
                      : 'bg-amber-500/10 border border-amber-500/20'
                  } flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <product.icon size={26} className={
                      product.color === 'emerald' ? 'text-emerald-400' : 'text-amber-400'
                    } />
                  </div>

                  {/* Title & Tagline */}
                  <h3 className="text-xl font-bold text-white font-['Outfit'] mb-1 group-hover:text-white/90">
                    {product.name}
                  </h3>
                  <p className={`text-xs font-medium ${
                    product.color === 'emerald' ? 'text-emerald-400' : 'text-amber-400'
                  } mb-3`}>
                    {product.tagline}
                  </p>

                  {/* Description */}
                  <p className="text-sm text-slate-400 leading-relaxed mb-5">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {product.features.map((feature, i) => (
                      <span
                        key={i}
                        className={`text-xs px-2.5 py-1 rounded-full ${
                          product.color === 'emerald'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className={`flex items-center gap-2 text-sm font-medium ${
                    product.color === 'emerald' ? 'text-emerald-400' : 'text-amber-400'
                  } group-hover:gap-3 transition-all duration-300`}>
                    Launch {product.name.split(' ').pop()}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4">
          <div className="max-w-6xl mx-auto flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <Coffee size={18} className="text-amber-500" />
              <span className="text-sm text-slate-500 font-['Outfit'] font-medium">FreeCoffee</span>
            </div>
            <p className="text-xs text-slate-600">
              © 2026 FreeCoffee. Crafted with ☕ for better productivity.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
